/**
 * XLSX reading, with no dependencies: a zip reader on top of the platform's
 * own `DecompressionStream`, and enough XML scanning to pull out cell values.
 *
 * Output matches ./xls.js: `{ sheets: [{ name, rows }] }`, where a cell is a
 * string, a number, a `YYYY-MM-DD` string for date-formatted cells, or null.
 */
import { DATE_FORMAT_IDS, isDateFormat, serialToISO } from './sheet-values'

const u16 = (b, o) => b[o] | (b[o + 1] << 8)
const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0

/** True when these bytes are a zip archive, which is what XLSX is. */
export function isZip(bytes) {
  return bytes[0] === 0x50 && bytes[1] === 0x4b
}

/* ------------------------------------------------------------------ */
/* zip                                                                 */
/* ------------------------------------------------------------------ */

async function inflateRaw(bytes) {
  if (typeof DecompressionStream !== 'function') {
    throw Object.assign(new Error('This browser cannot unpack .xlsx files — save the sheet as .xls or .csv'), { code: 'sheet.cannot-unzip' })
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** Reads the central directory and returns a map of entry name -> bytes. */
async function readZip(bytes) {
  let eocd = -1
  for (let at = bytes.length - 22; at >= 0 && at > bytes.length - 65558; at--) {
    if (u32(bytes, at) === 0x06054b50) {
      eocd = at
      break
    }
  }
  if (eocd < 0) throw Object.assign(new Error('Not a readable .xlsx file'), { code: 'sheet.bad-xlsx' })

  const count = u16(bytes, eocd + 10)
  let at = u32(bytes, eocd + 16)
  const files = new Map()
  const decoder = new TextDecoder()

  for (let i = 0; i < count && at + 46 <= bytes.length; i++) {
    if (u32(bytes, at) !== 0x02014b50) break
    const method = u16(bytes, at + 10)
    const compressed = u32(bytes, at + 20)
    const nameLength = u16(bytes, at + 28)
    const extraLength = u16(bytes, at + 30)
    const commentLength = u16(bytes, at + 32)
    const localAt = u32(bytes, at + 42)
    const name = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength))

    const dataAt = localAt + 30 + u16(bytes, localAt + 26) + u16(bytes, localAt + 28)
    const raw = bytes.subarray(dataAt, dataAt + compressed)
    files.set(name, method === 0 ? raw : await inflateRaw(raw))
    at += 46 + nameLength + extraLength + commentLength
  }
  return files
}

/* ------------------------------------------------------------------ */
/* XML                                                                 */
/* ------------------------------------------------------------------ */

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }

function unescapeXml(text) {
  if (!text.includes('&')) return text
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-z]+);/g, (whole, body) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' ? parseInt(body.slice(2), 16) : Number(body.slice(1))
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole
    }
    return ENTITIES[body] ?? whole
  })
}

const attr = (tag, name) => {
  const match = tag.match(new RegExp(`\\b${name}\\s*=\\s*"([^"]*)"`))
  return match ? unescapeXml(match[1]) : null
}

/** Concatenates every `<t>` run inside a chunk, which is how rich text arrives. */
function textOf(chunk) {
  let out = ''
  for (const match of chunk.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>|<t\b[^>]*\/>/g)) {
    out += unescapeXml(match[1] ?? '')
  }
  return out
}

/** "BC12" -> 54 */
function columnIndex(ref) {
  let index = 0
  for (const ch of ref) {
    const code = ch.charCodeAt(0)
    if (code < 65 || code > 90) break
    index = index * 26 + (code - 64)
  }
  return index - 1
}

/* ------------------------------------------------------------------ */
/* workbook                                                            */
/* ------------------------------------------------------------------ */

/**
 * @param {Uint8Array} bytes
 * @returns {Promise<{ sheets: { name: string, rows: (string|number|null)[][] }[] }>}
 */
export async function readXlsx(bytes) {
  const files = await readZip(bytes)
  const decoder = new TextDecoder()
  const text = (name) => (files.has(name) ? decoder.decode(files.get(name)) : '')

  const workbook = text('xl/workbook.xml')
  if (!workbook) throw Object.assign(new Error('This file is a zip, but not a spreadsheet'), { code: 'sheet.zip-not-sheet' })
  const date1904 = /date1904="(1|true)"/.test(workbook)

  const shared = []
  for (const match of text('xl/sharedStrings.xml').matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>|<si\b[^>]*\/>/g)) {
    shared.push(match[1] ? textOf(match[1]) : '')
  }

  const styles = text('xl/styles.xml')
  const customFormats = new Map()
  for (const match of styles.matchAll(/<numFmt\b[^>]*\/>/g)) {
    customFormats.set(Number(attr(match[0], 'numFmtId')), attr(match[0], 'formatCode') ?? '')
  }
  const cellXfs = styles.match(/<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/)
  const dateXf = [...(cellXfs?.[1] ?? '').matchAll(/<xf\b[^>]*>/g)].map((match) => {
    const id = Number(attr(match[0], 'numFmtId') ?? 0)
    return customFormats.has(id) ? isDateFormat(customFormats.get(id)) : DATE_FORMAT_IDS.has(id)
  })

  const rels = new Map()
  for (const match of text('xl/_rels/workbook.xml.rels').matchAll(/<Relationship\b[^>]*\/>/g)) {
    rels.set(attr(match[0], 'Id'), attr(match[0], 'Target'))
  }

  const sheets = []
  for (const match of workbook.matchAll(/<sheet\b[^>]*\/>/g)) {
    const tag = match[0]
    const target = rels.get(attr(tag, 'r:id') ?? attr(tag, 'id'))
    const path = target
      ? `xl/${String(target).replace(/^\/?(xl\/)?/, '')}`
      : `xl/worksheets/sheet${sheets.length + 1}.xml`
    if (!files.has(path)) continue
    sheets.push({ name: attr(tag, 'name') ?? `Sheet${sheets.length + 1}`, rows: readSheet(text(path)) })
  }

  if (!sheets.length) throw Object.assign(new Error('This workbook has no worksheets'), { code: 'sheet.no-worksheets' })
  return { sheets }

  function readSheet(xml) {
    const rows = []
    for (const rowMatch of xml.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
      const at = Number(attr(`<row${rowMatch[1]}>`, 'r')) - 1
      const line = []
      for (const cellMatch of rowMatch[2].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const tag = `<c${cellMatch[1]}>`
        const body = cellMatch[2] ?? ''
        const ref = attr(tag, 'r')
        const col = ref ? columnIndex(ref) : line.length
        while (line.length < col) line.push(null)
        line[col] = cellValue(tag, body)
      }
      rows[Number.isFinite(at) && at >= 0 ? at : rows.length] = line
    }
    const width = rows.reduce((w, row) => Math.max(w, row?.length ?? 0), 0)
    return rows.map((row) => {
      const line = row ?? []
      return Array.from({ length: width }, (_, i) => line[i] ?? null)
    })
  }

  function cellValue(tag, body) {
    const type = attr(tag, 't') ?? 'n'
    if (type === 'inlineStr') return textOf(body)
    const raw = unescapeXml(body.match(/<v\b[^>]*>([\s\S]*?)<\/v>/)?.[1] ?? '')
    if (type === 's') return shared[Number(raw)] ?? ''
    if (type === 'str') return raw
    if (type === 'b') return raw === '1'
    if (type === 'e') return null
    if (raw === '') return null
    const value = Number(raw)
    if (!Number.isFinite(value)) return raw
    const style = Number(attr(tag, 's') ?? -1)
    return dateXf[style] ? (serialToISO(value, date1904) ?? value) : value
  }
}
